import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { memo } from 'react';
import { shallowEqual } from 'react-redux';
import { UserType, WorkspaceParticipant } from 'src/@types/workspace-info';
import {
  CustomAvatar,
  CustomAvatarGroup,
  CustomAvatarGroupProps,
} from 'src/components/custom-avatar';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useSelector } from 'src/redux/store';

type WorkspaceShareMembersAvatarProps = {
  members: WorkspaceParticipant[];
  size: CustomAvatarGroupProps['size'];
  max?: number;
  compact?: boolean;
};

export const WorkspaceShareMembersAvatar = memo(
  ({
    members,
    size,
    max = 3,
    compact = false,
  }: WorkspaceShareMembersAvatarProps) => {
    const { isSmallMobile } = useBreakpoints();
    const usersById = useSelector(
      (state) => state.workspace.usersById,
      shallowEqual,
    );
    const teamsById = useSelector(
      (state) => state.workspace.teamsById,
      shallowEqual,
    );

    return (
      <CustomAvatarGroup
        max={isSmallMobile ? 3 : max}
        size={isSmallMobile ? 'tiny' : size}
        compact={compact}
      >
        {members?.map((item) => {
          if (
            'user' in item &&
            usersById[item.user] &&
            usersById[item.user].type !== UserType.AI &&
            usersById[item.user].type !== UserType.INTEGRATION
          )
            return (
              <UserProfileShortPopoverTrigger
                userId={item.user}
                key={item.user}
              >
                <CustomAvatar
                  key={item.user}
                  name={usersById[item.user].name}
                  src={usersById[item.user].picture}
                  variant="circular"
                />
              </UserProfileShortPopoverTrigger>
            );
          if ('team' in item && teamsById[item.team])
            return (
              <CustomAvatar
                key={item.team}
                name={teamsById[item.team].name}
                src={teamsById[item.team].picture}
                variant="rounded"
              />
            );

          return null;
        })}
      </CustomAvatarGroup>
    );
  },
  // update only if members items change
  (prev, next) =>
    prev.members?.map((_: any) => _.user || _.team).join() ===
    next.members?.map((_: any) => _.user || _.team).join(),
);
